import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColorsAlt,
} from 'src/app/shared/components/map/map.constants';
import { PathOptions } from 'leaflet';
import { MatTableDataSource } from '@angular/material/table';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import {
    VOLTAGE_QUALITY_COMPARISON_CHART,
    VOLTAGE_QUALITY_EVOL_LV_DEPART_CHART,
    VOLTAGE_QUALITY_EVOL_MV_DEPART_CHART,
    VOLTAGE_QUALITY_EVOL_RATE_CMA_CHART,
    VOLTAGE_QUALITY_KPIS,
    VOLTAGE_QUALITY_LV_CMA_RATE_MAP,
    VOLTAGE_QUALITY_MV_DEPARTURES_TABLE,
    VOLTAGE_QUALITY_SUBSTATION_CMA_TABLE,
} from '../../assets/keys';

const KEYS = [
    VOLTAGE_QUALITY_KPIS,
    VOLTAGE_QUALITY_COMPARISON_CHART,
    VOLTAGE_QUALITY_EVOL_MV_DEPART_CHART,
    VOLTAGE_QUALITY_EVOL_LV_DEPART_CHART,
    VOLTAGE_QUALITY_EVOL_RATE_CMA_CHART,
    VOLTAGE_QUALITY_MV_DEPARTURES_TABLE,
    VOLTAGE_QUALITY_SUBSTATION_CMA_TABLE,
    VOLTAGE_QUALITY_LV_CMA_RATE_MAP,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    mvDeparturesRateAbove5Percent?: number;
    mvDeparturesRateAbove5PercentEvolution?: number;

    lvCmaRate?: number;
    lvCmaRateEvolution?: number;

    numberOfLvCma?: number;
    numberOfLvCmaEvolution?: number;

    numberOfLvDma?: number;
    numberOfLvDmaEvolution?: number;
}

interface VoltageQualityComparisonChartData {
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=308
    nbrMvDepartMaxCT5And7Percent?: number;
    //kpi_id=290
    nbrMvDepartMaxCTAbove7Percent?: number;
    //kpi_id=292
    rateMvDepartCtAbove5Percent?: number;
    error?: boolean;
}

interface LvDepartChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=294
    nbrLvDepartPoorEr?: number;
    //kpi_id=296
    nbrLvDepartPoorRu?: number;
    //kpi_id=299
    rateDmaAllZone?: number;
    error?: boolean;
}

interface RateCmaChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=300
    nbrClientPoorEr?: number;
    //kpi_id=302
    nbrClientPoorRu?: number;
    //kpi_id=305
    rateCmaTot?: number;
    error?: boolean;
}

interface MvDeparturesTableData {
    organizationId?: string;
    year?: string;
    sourceStationName?: string;
    departureName?: string;
    maxVoltageDrop?: number;
    previousYearMaxVoltageDrop?: number;
    error?: boolean;
}

interface SubstationCmaTableData {
    organizationId?: string;
    year?: string;
    cityName?: string;
    inseeCode?: string;
    subStationName?: string;
    gdoCode?: string;
    category?: string;
    cmaCount?: number;
    previousYearCmaCount?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-voltage-quality',
    templateUrl: './voltage-quality.component.html',
    styleUrls: ['./voltage-quality.component.scss'],
})
export class VoltageQualityComponent extends GenericComponent {
    public texts = CC_CONSTANTS.voltageQuality.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_KPIS),
    ) as Signal<KpiData[]>;

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_EVOL_MV_DEPART_CHART),
    ) as Signal<ChartData[]>;
    public chartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: 1 }),
    };
    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [];
    public chartError: boolean = true;
    public mvRateCustomTooltip: ApexTooltip['custom'];

    public lvDepartChartData: Signal<LvDepartChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_EVOL_LV_DEPART_CHART),
    ) as Signal<LvDepartChartData[]>;
    public lvDepartChartSeries: ApexAxisChartSeries = [];
    public lvDepartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
        style: this.setDataLabelsStyleColors(
            this.texts.evolutionNbrLvDepartPoorChart.totalDmaTitle,
        ),
    };
    public lvDepartLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec3Green, colorAec1BlueLight, colorBlack, colorAec8RedDark],
        },
    };
    public lvDepartXaxisCategories: ApexXAxis['categories'];
    public lvDepartYaxis: ApexYAxis[] = [];
    public lvDepartChartError: boolean = true;
    public lvDepartCustomTooltip: ApexTooltip['custom'];

    public rateCmaChartData: Signal<RateCmaChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_EVOL_RATE_CMA_CHART),
    ) as Signal<RateCmaChartData[]>;
    public rateCmaChartSeries: ApexAxisChartSeries = [];
    public rateCmaDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3]),
        style: this.setDataLabelsStyleColors(this.texts.evolutionNbrRateCmaChart.totalCmaTitle),
    };
    public rateCmaLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec3Green, colorAec1BlueLight, colorBlack, colorAec8RedDark],
        },
    };
    public rateCmaXaxisCategories: ApexXAxis['categories'];
    public rateCmaYaxis: ApexYAxis[] = [];
    public rateCmaChartError: boolean = true;
    public rateCmaCustomTooltip: ApexTooltip['custom'];

    public voltageQualityComparisonChartDataSignal: Signal<VoltageQualityComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_COMPARISON_CHART),
        ) as Signal<VoltageQualityComparisonChartData[]>;
    public voltageQualityComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public voltageQualityComparisonChartSeries: ApexAxisChartSeries = [];
    public voltageQualityComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(3),
            borderRadius: 1,
        },
    };
    public voltageQualityComparisonCustomTooltip: ApexTooltip['custom'] = function ({
        series,
        seriesIndex,
        dataPointIndex,
        w,
    }) {
        const precision = CC_CONSTANTS.voltageQuality.texts.voltageQualityComparisonChart.precision;
        const dataSerie = w.globals.initialSeries[seriesIndex];
        const serieName = dataSerie.name;
        const serieColor = dataSerie.color;
        const dataPoint = dataSerie.data[dataPointIndex];
        const min = convertNumber(dataPoint.y[0], precision, false);
        const max = convertNumber(dataPoint.y[1], precision, false);

        const meanGoal = dataPoint.goals[0];
        const meanValue = convertNumber(meanGoal.value, precision, false);
        const meanName = meanGoal.name;
        const meanColor = meanGoal.strokeColor;

        const clientValueGoal = dataPoint.goals[1];
        const clientValue = convertNumber(clientValueGoal.value, precision, false);
        const clientValueName = clientValueGoal.name;
        const clientColor = clientValueGoal.strokeColor;

        return `
            <div class="apexcharts-tooltip-title">
                ${dataPoint.x}
            </div>

            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} %</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${meanValue} %</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${clientValue} %</span>
                    </div>

                </div>
            </div>
        `;
    };

    public yaxisLvDepart: ApexYAxis[] = [
        {
            seriesName: this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorErAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorErAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolutionNbrLvDepartPoorChart.rateDmaAllZoneAxisTitle,
            min: 0,
            max: 3,
            opposite: true,
            labels: {
                formatter: (value: number) => convertNumber(value),
            },
        },
    ];

    public yaxisRateCma: ApexYAxis[] = [
        {
            seriesName: this.texts.evolutionNbrRateCmaChart.nbrClientPoorErAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolutionNbrRateCmaChart.nbrClientPoorErAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolutionNbrRateCmaChart.rateCmaTotAxisTitle,
            min: 0,
            opposite: true,
            labels: {
                formatter: (value: number) => convertNumber(value),
            },
        },
    ];

    public voltageQualityComparisonChartError: boolean = true;

    public mvDeparturesTableData: Signal<MvDeparturesTableData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_MV_DEPARTURES_TABLE),
    ) as Signal<MvDeparturesTableData[]>;

    public mvDeparturesTableError = true;
    public mvDeparturesTableDataSource!: MatTableDataSource<MvDeparturesTableData>;
    public mvDeparturesDisplayedColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.mvDeparturesTable.sourceStationNameTitle,
            objectKey: 'sourceStationName',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.mvDeparturesTable.departureNameTitle,
            objectKey: 'departureName',
            bold: true,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: this.texts.mvDeparturesTable.previousYearMaxVoltageDropTitle,
            objectKey: 'previousYearMaxVoltageDrop',
            precision: 2,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: this.texts.mvDeparturesTable.maxVoltageDropTitle,
            objectKey: 'maxVoltageDrop',
            precision: 2,
        },
    ];

    public substationCmaTableData: Signal<SubstationCmaTableData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_SUBSTATION_CMA_TABLE),
    ) as Signal<SubstationCmaTableData[]>;
    public substationCmaTableError = true;
    public substationCmaTableDataSource!: MatTableDataSource<SubstationCmaTableData>;
    public subStationCmaDisplayedColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.substationCmaTable.cityNameTitle,
            objectKey: 'cityName',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.substationCmaTable.inseeCodeTitle,
            objectKey: 'inseeCode',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.substationCmaTable.subStationNameTitle,
            objectKey: 'subStationName',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.substationCmaTable.gdoCodeTitle,
            objectKey: 'gdoCode',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: this.texts.substationCmaTable.categoryTitle,
            objectKey: 'category',
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: this.texts.substationCmaTable.previousYearCmaCountTitle,
            objectKey: 'previousYearCmaCount',
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: this.texts.substationCmaTable.cmaCountTitle,
            objectKey: 'cmaCount',
        },
    ];

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(VOLTAGE_QUALITY_LV_CMA_RATE_MAP),
    ) as Signal<any[]>;
    public legendConfig: LegendConfig[] = [
        {
            title: 'Taux de Clients BT considérés Mal Alimentés (CMA BT)',
            values: [
                { color: MapColorsAlt.darkGreen, value: 'Moins de 0,5%' },
                { color: MapColorsAlt.lightGreen, value: 'Entre 0,5 et 1%' },
                { color: MapColorsAlt.lighterGreen, value: 'Entre 1 et 1,5%' },
                { color: MapColorsAlt.paleGreen, value: 'Entre 1,5 et 2%' },
                { color: colorAec4Orange, value: 'Entre 2 et 2,5%' },
                { color: colorAec5Brown, value: 'Entre 2,5 et 3%' },
                { color: colorAec8RedDark, value: 'Plus de 3%' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            this.chartError = this.checkForError(dataList, [
                'xYear',
                'nbrMvDepartMaxCT5And7Percent',
                'nbrMvDepartMaxCTAbove7Percent',
                'rateMvDepartCtAbove5Percent',
            ]);
            if (!this.chartError) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                const totalMax = dataList.reduce(
                    (max, d) =>
                        Math.max(
                            max,
                            (d.nbrMvDepartMaxCT5And7Percent ?? 0) +
                                (d.nbrMvDepartMaxCTAbove7Percent ?? 0),
                        ),
                    -Infinity,
                );
                const rateMax = dataList.reduce(
                    (max, d) => Math.max(max, d.rateMvDepartCtAbove5Percent ?? 0),
                    -Infinity,
                );

                this.yaxis = [
                    {
                        seriesName: [
                            this.texts.evolutionMvDepartCtAbove5PercentChart
                                .nbrMvDepartMaxCT5And7PercentAxisTitle,
                            this.texts.evolutionMvDepartCtAbove5PercentChart
                                .nbrMvDepartMaxCTAbove7PercentAxisTitle,
                        ],
                        min: 0,
                        max: Math.ceil(totalMax / 4 + 1) * 4,
                        forceNiceScale: true,
                        tickAmount: 4,
                        title: {
                            text: this.texts.evolutionMvDepartCtAbove5PercentChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName:
                            this.texts.evolutionMvDepartCtAbove5PercentChart
                                .rateMvDepartCtAbove5PercentAxisTitle,
                        min: 0,
                        max: Math.ceil(rateMax / 4 + 1) * 4,
                        opposite: true,
                        title: {
                            text: this.texts.evolutionMvDepartCtAbove5PercentChart.yaxisTitleRight,
                        },
                    },
                ];
                this.chartSeries = [
                    {
                        name: this.texts.evolutionMvDepartCtAbove5PercentChart
                            .nbrMvDepartMaxCT5And7PercentAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.nbrMvDepartMaxCT5And7Percent!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.evolutionMvDepartCtAbove5PercentChart
                            .nbrMvDepartMaxCTAbove7PercentAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.nbrMvDepartMaxCTAbove7Percent!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolutionMvDepartCtAbove5PercentChart
                            .rateMvDepartCtAbove5PercentAxisTitle,
                        type: 'line',
                        data: dataList.map((data) =>
                            parseFloat(data.rateMvDepartCtAbove5Percent!.toFixed(1)),
                        ),
                        color: colorAec8RedDark,
                    },
                ];
                this.dataLabels.enabledOnSeries = [0, 1, 2];
            }

            const dataListLvDepart = this.lvDepartChartData();
            this.lvDepartChartError = this.checkForError(dataListLvDepart, [
                'xYear',
                'nbrLvDepartPoorEr',
                'nbrLvDepartPoorRu',
                'rateDmaAllZone',
            ]);
            if (!this.lvDepartChartError) {
                this.lvDepartXaxisCategories = dataListLvDepart.map((data) => data.xYear);

                const totales = this.getTotales(dataListLvDepart, [
                    'nbrLvDepartPoorEr',
                    'nbrLvDepartPoorRu',
                ]);
                const totalMax = totales.reduce((max, t) => Math.max(max, t), -Infinity);
                const rateMin = dataListLvDepart.reduce(
                    (m, d) => Math.min(m, d.rateDmaAllZone ?? 0),
                    Infinity,
                );
                const rateMax = dataListLvDepart.reduce(
                    (m, d) => Math.max(m, d.rateDmaAllZone ?? 0),
                    -Infinity,
                );

                this.lvDepartYaxis = [
                    {
                        seriesName: [
                            this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorErAxisTitle,
                            this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorRuAxisTitle,
                            this.texts.evolutionNbrLvDepartPoorChart.totalDmaTitle,
                        ],
                        title: {
                            text: this.texts.evolutionNbrLvDepartPoorChart.yaxisTitleLeft,
                        },
                        min: 0,
                        max: Math.round(totalMax / 4 + 1) * 4,
                        forceNiceScale: true,
                        tickAmount: 4,
                    },
                    {
                        seriesName:
                            this.texts.evolutionNbrLvDepartPoorChart.rateDmaAllZoneAxisTitle,
                        min: Math.ceil(rateMin / 4 - 1) * 4,
                        max: Math.round(rateMax / 4 + 1) * 4,
                        forceNiceScale: true,
                        tickAmount: 4,
                        opposite: true,
                        labels: {
                            formatter: (value: number) => value.toString(),
                        },
                        title: {
                            text: this.texts.evolutionNbrLvDepartPoorChart.yaxisTitleRight,
                        },
                    },
                ];
                this.lvDepartChartSeries = [
                    {
                        name: this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorErAxisTitle,
                        type: 'column',
                        data: dataListLvDepart.map((data) => data.nbrLvDepartPoorEr!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolutionNbrLvDepartPoorChart.nbrLvDepartPoorRuAxisTitle,
                        type: 'column',
                        data: dataListLvDepart.map((data) => data.nbrLvDepartPoorRu!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolutionNbrLvDepartPoorChart.totalDmaTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.evolutionNbrLvDepartPoorChart.rateDmaAllZoneAxisTitle,
                        type: 'line',
                        data: dataListLvDepart.map((data) => data.rateDmaAllZone!),
                        color: colorAec8RedDark,
                    },
                ];
                this.lvDepartDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const dataListRateCma = this.rateCmaChartData();
            this.rateCmaChartError = this.checkForError(dataListRateCma, [
                'xYear',
                'nbrClientPoorEr',
                'nbrClientPoorRu',
                'rateCmaTot',
            ]);
            if (!this.rateCmaChartError) {
                this.rateCmaXaxisCategories = dataList.map((data) => data.xYear);
                const totales = this.getTotales(dataListRateCma, [
                    'nbrClientPoorEr',
                    'nbrClientPoorRu',
                ]);

                const totalesMax = totales.reduce((m, t) => Math.max(m, t), -Infinity);
                const rateMin = dataListRateCma.reduce(
                    (m, d) => Math.min(m, d.rateCmaTot ?? 0),
                    Infinity,
                );
                const rateMax = dataListRateCma.reduce(
                    (m, d) => Math.max(m, d.rateCmaTot ?? 0),
                    -Infinity,
                );
                this.rateCmaYaxis = [
                    {
                        seriesName: [
                            this.texts.evolutionNbrRateCmaChart.nbrClientPoorRuAxisTitle,
                            this.texts.evolutionNbrRateCmaChart.nbrClientPoorErAxisTitle,
                            this.texts.evolutionNbrRateCmaChart.totalCmaTitle,
                        ],
                        title: {
                            text: this.texts.evolutionNbrRateCmaChart.yaxisTitleLeft,
                        },
                        min: 0,
                        max: Math.round(totalesMax / 40 + 1) * 40,
                        forceNiceScale: true,
                        tickAmount: 4,
                    },
                    {
                        seriesName: [this.texts.evolutionNbrRateCmaChart.rateCmaTotAxisTitle],
                        min: Math.floor(rateMin) - (4 - (Math.floor(rateMin) % 4)),
                        max: Math.ceil(rateMax) + (4 - (Math.ceil(rateMax) % 4)),
                        opposite: true,
                        tickAmount: 4,
                        labels: {
                            formatter: (value: number) => convertNumber(value, null),
                        },
                        title: {
                            text: this.texts.evolutionNbrRateCmaChart.yaxisTitleRight,
                        },
                    },
                ];
                this.rateCmaChartSeries = [
                    {
                        name: this.texts.evolutionNbrRateCmaChart.nbrClientPoorErAxisTitle,
                        type: 'column',
                        data: dataListRateCma.map((data) => data.nbrClientPoorEr!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolutionNbrRateCmaChart.nbrClientPoorRuAxisTitle,
                        type: 'column',
                        data: dataListRateCma.map((data) => data.nbrClientPoorRu!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolutionNbrRateCmaChart.totalCmaTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.evolutionNbrRateCmaChart.rateCmaTotAxisTitle,
                        type: 'line',
                        data: dataListRateCma.map((data) => data.rateCmaTot!),
                        color: colorAec8RedDark,
                    },
                ];
                this.rateCmaDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const rateComparisonChartData = this.voltageQualityComparisonChartDataSignal();
            this.voltageQualityComparisonChartError = this.checkForError(rateComparisonChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.voltageQualityComparisonChartError) {
                this.voltageQualityComparisonChartSeries = [
                    {
                        name: this.texts.voltageQualityComparisonChart.serieLabel,
                        data: rateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.voltageQualityComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.voltageQualityComparisonChart.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.voltageQualityComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.voltageQualityComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const mvDeparturesTableData = this.mvDeparturesTableData();
            this.mvDeparturesTableError = this.checkForError(mvDeparturesTableData, [
                'sourceStationName',
                'departureName',
                'maxVoltageDrop',
                'previousYearMaxVoltageDrop',
            ]);

            if (!this.mvDeparturesTableError) {
                this.mvDeparturesTableDataSource = new MatTableDataSource<MvDeparturesTableData>(
                    mvDeparturesTableData.sort(
                        (a, b) => (b.maxVoltageDrop || 0) - (a.maxVoltageDrop || 0),
                    ),
                );
            }

            const substationCmaTableData = this.substationCmaTableData();
            this.substationCmaTableError = this.checkForError(substationCmaTableData, [
                'cityName',
                'inseeCode',
                'subStationName',
                'gdoCode',
                'category',
                'cmaCount',
                'previousYearCmaCount',
            ]);

            if (!this.substationCmaTableError) {
                this.substationCmaTableDataSource = new MatTableDataSource<SubstationCmaTableData>(
                    substationCmaTableData
                        .map((data) => {
                            data.category = data.category === 'R' ? 'Rural' : 'Urbain';
                            return data;
                        })
                        .sort((a, b) => (b.cmaCount || 0) - (a.cmaCount || 0)),
                );
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.mvRateCustomTooltip = this.setMvRateCustomTooltip();
        this.lvDepartCustomTooltip = this.setLvDepartCustomTooltip();
        this.rateCmaCustomTooltip = this.setRateCmaCustomTooltip();
    }

    public setMvRateCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolutionMvDepartCtAbove5PercentChart.precision;
            const mvAboveFiveSerie = w.globals.initialSeries[0];
            const {
                name: mvAboveFiveName,
                color: mvAboveFiveColor,
                valueStringified: mvAboveFiveStringified,
            } = this.setCustomTooltipData(precision, mvAboveFiveSerie, dataPointIndex);

            const mvAboveSevenSerie = w.globals.initialSeries[1];
            const {
                name: mvAboveSevenName,
                color: mvAboveSevenColor,
                valueStringified: mvAboveSevenStringified,
            } = this.setCustomTooltipData(precision, mvAboveSevenSerie, dataPointIndex);

            const ratePrecision = this.texts.evolutionMvDepartCtAbove5PercentChart.ratePrecision;
            const rateSerie = w.globals.initialSeries[2];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: mvAboveFiveColor,
                    name: mvAboveFiveName,
                    value: mvAboveFiveStringified + ' départs HTA',
                },
                {
                    color: mvAboveSevenColor,
                    name: mvAboveSevenName,
                    value: mvAboveSevenStringified + ' départs HTA',
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setLvDepartCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolutionNbrLvDepartPoorChart.precision;
            const erSerie = w.globals.initialSeries[0];
            const {
                name: erName,
                color: erColor,
                valueStringified: erStringified,
            } = this.setCustomTooltipData(precision, erSerie, dataPointIndex);

            const ruSerie = w.globals.initialSeries[1];
            const {
                name: ruName,
                color: ruColor,
                valueStringified: ruStringified,
            } = this.setCustomTooltipData(precision, ruSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[2];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.evolutionNbrLvDepartPoorChart.ratePrecision;
            const rateSerie = w.globals.initialSeries[3];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: erColor,
                    name: erName,
                    value: erStringified,
                },
                {
                    color: ruColor,
                    name: ruName,
                    value: ruStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setRateCmaCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolutionNbrRateCmaChart.precision;
            const erSerie = w.globals.initialSeries[0];
            const {
                name: erName,
                color: erColor,
                valueStringified: erStringified,
            } = this.setCustomTooltipData(precision, erSerie, dataPointIndex);

            const ruSerie = w.globals.initialSeries[1];
            const {
                name: ruName,
                color: ruColor,
                valueStringified: ruStringified,
            } = this.setCustomTooltipData(precision, ruSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[2];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.evolutionNbrRateCmaChart.ratePrecision;
            const rateSerie = w.globals.initialSeries[3];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: erColor,
                    name: erName,
                    value: erStringified,
                },
                {
                    color: ruColor,
                    name: ruName,
                    value: ruStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['lvCmaRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColorsAlt.darkGreen;
        const lvCmaRate = Number(feature.properties['lvCmaRate']);
        if (lvCmaRate > 3) {
            fillColor = colorAec8RedDark;
        } else if (lvCmaRate > 25) {
            fillColor = colorAec5Brown;
        } else if (lvCmaRate > 2) {
            fillColor = colorAec4Orange;
        } else if (lvCmaRate > 15) {
            fillColor = MapColorsAlt.paleGreen;
        } else if (lvCmaRate > 1) {
            fillColor = MapColorsAlt.lighterGreen;
        } else if (lvCmaRate > 0.5) {
            fillColor = MapColorsAlt.lightGreen;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, lvCmaRate, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const valueStringified = convertNumber(lvCmaRate, null);
        return `${cityName} - ${valueStringified} % de CMA BT`;
    }
}
